import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Form, Alert, Spinner, Modal, Button, FloatingLabel } from 'react-bootstrap';
import messagingApi from '../../services/messagingApi';
import TemplateCard from './TemplateCard';
import TestMessageModal from './TestMessageModal';

const TemplatesTab = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showTestModal, setShowTestModal] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);

  // Filters — default the الحالة filter to active templates (نشط)
  const [filters, setFilters] = useState({
    category: '',
    active: 'true'
  });

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await messagingApi.getTemplates(filters);
      setTemplates(data.templates || []);
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError('فشل في تحميل القوالب');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleTestMessage = (template) => {
    setSelectedTemplate(template);
    setShowTestModal(true);
  };

  const handleEditTemplate = (template) => {
    setEditTemplate(JSON.parse(JSON.stringify(template)));
    setShowEditModal(true);
  };

  const handleCloseTestModal = () => {
    setShowTestModal(false);
    setSelectedTemplate(null);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditTemplate(null);
    setSavingEdit(false);
  };

  const handleSaveEdit = async () => {
    if (!editTemplate?._id) return;
    try {
      setSavingEdit(true);
      // For an OTA template, FORCE whatsapp/sms off rather than trusting the disabled switches:
      // a value set outside this screen (API, DB, seed) would otherwise survive every save while
      // the UI claims those channels are disabled. The backend enforces the same invariant.
      const isOta = !!editTemplate.channels?.ota?.enabled;
      const channels = isOta
        ? {
            ...(editTemplate.channels || {}),
            whatsapp: { ...(editTemplate.channels?.whatsapp || {}), enabled: false },
            sms: { ...(editTemplate.channels?.sms || {}), enabled: false }
          }
        : editTemplate.channels;

      const payload = {
        name: editTemplate.name,
        description: editTemplate.description,
        active: !!editTemplate.active,
        channels,
        content: editTemplate.content
      };
      await messagingApi.updateTemplate(editTemplate._id, payload);
      await fetchTemplates();
      handleCloseEditModal();
    } catch (err) {
      console.error('Error updating template:', err);
      setError('فشل في تحديث القالب');
      setSavingEdit(false);
    }
  };

  const handleTestMessageSent = () => {
    // Optionally refresh templates to update stats
    fetchTemplates();
  };

  if (loading && templates.length === 0) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2 text-muted">جاري تحميل القوالب...</p>
      </div>
    );
  }

  // Is the template currently open in the edit modal an OTA-channel one? Same derivation as
  // TemplateCard — driven by the channel flag, never a hardcoded templateId.
  const isOtaTemplate = !!editTemplate?.channels?.ota?.enabled;

  return (
    <div dir="rtl">
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters Bar */}
      <Row className="mb-4 align-items-end">
        <Col md={3}>
          <Form.Group>
            <Form.Label>الفئة</Form.Label>
            <Form.Select
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              dir="rtl"
            >
              <option value="">الكل</option>
              <option value="guest">رسائل الضيوف</option>
              <option value="staff">رسائل الموظفين</option>
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={3}>
          <Form.Group>
            <Form.Label>الحالة</Form.Label>
            <Form.Select
              value={filters.active}
              onChange={(e) => handleFilterChange('active', e.target.value)}
              dir="rtl"
            >
              <option value="">الكل</option>
              <option value="true">نشط</option>
              <option value="false">غير نشط</option>
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      {/* Templates Grid */}
      {templates.length === 0 ? (
        <div className="text-center py-5">
          <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#ccc' }}></i>
          <p className="mt-3 text-muted">لا توجد قوالب</p>
        </div>
      ) : (
        <Row>
          {templates.map((template) => (
            <Col key={template._id} xs={12} md={6} lg={4} className="mb-4">
              <TemplateCard
                template={template}
                onTestMessage={handleTestMessage}
                onEdit={handleEditTemplate}
              />
            </Col>
          ))}
        </Row>
      )}

      {/* Test Message Modal */}
      {selectedTemplate && (
        <TestMessageModal
          show={showTestModal}
          onHide={handleCloseTestModal}
          template={selectedTemplate}
          onMessageSent={handleTestMessageSent}
        />
      )}

      {editTemplate && (
        <Modal show={showEditModal} onHide={handleCloseEditModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>تعديل القالب</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {/* Save errors must render INSIDE the modal — the page-level Alert sits behind the
                backdrop, so a failed save looked like nothing happened at all. */}
            {error && (
              <Alert variant="danger" dismissible onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            <Row className="mb-3">
              <Col md={6}>
                <FloatingLabel label="اسم القالب (عربي)">
                  <Form.Control
                    type="text"
                    value={editTemplate.name?.ar || ''}
                    onChange={(e) => setEditTemplate(prev => ({
                      ...prev,
                      name: { ...(prev.name || {}), ar: e.target.value }
                    }))}
                  />
                </FloatingLabel>
              </Col>
              <Col md={6}>
                <FloatingLabel label="اسم القالب (إنجليزي)">
                  <Form.Control
                    type="text"
                    dir="ltr"
                    value={editTemplate.name?.en || ''}
                    onChange={(e) => setEditTemplate(prev => ({
                      ...prev,
                      name: { ...(prev.name || {}), en: e.target.value }
                    }))}
                  />
                </FloatingLabel>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <FloatingLabel label="الوصف (عربي)">
                  <Form.Control
                    type="text"
                    value={editTemplate.description?.ar || ''}
                    onChange={(e) => setEditTemplate(prev => ({
                      ...prev,
                      description: { ...(prev.description || {}), ar: e.target.value }
                    }))}
                  />
                </FloatingLabel>
              </Col>
              <Col md={6}>
                <FloatingLabel label="الوصف (إنجليزي)">
                  <Form.Control
                    type="text"
                    dir="ltr"
                    value={editTemplate.description?.en || ''}
                    onChange={(e) => setEditTemplate(prev => ({
                      ...prev,
                      description: { ...(prev.description || {}), en: e.target.value }
                    }))}
                  />
                </FloatingLabel>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={4}>
                <Form.Check
                  type="switch"
                  id="template-active"
                  label="نشط"
                  checked={!!editTemplate.active}
                  onChange={(e) => setEditTemplate(prev => ({
                    ...prev,
                    active: e.target.checked
                  }))}
                />
              </Col>
              {/* OTA templates (ota_checkin_instructions / ota_checkout_reminder) are posted to the
                  guest's Airbnb/Booking.com thread by their own standalone crons. WhatsApp/SMS are
                  locked OFF for them on purpose: the DORMANT legacy scheduler picks a channel by
                  checking whatsapp first, so enabling it here would arm an OTA template to be sent
                  over WhatsApp if that stack were ever switched back on. Disabled at the source
                  rather than tolerated and worked around downstream. */}
              <Col md={4}>
                <Form.Check
                  type="switch"
                  id="channel-whatsapp"
                  label="واتساب"
                  checked={isOtaTemplate ? false : !!editTemplate.channels?.whatsapp?.enabled}
                  disabled={isOtaTemplate}
                  aria-describedby={isOtaTemplate ? 'ota-channel-note' : undefined}
                  onChange={(e) => setEditTemplate(prev => ({
                    ...prev,
                    channels: {
                      ...(prev.channels || {}),
                      whatsapp: { ...(prev.channels?.whatsapp || {}), enabled: e.target.checked }
                    }
                  }))}
                />
              </Col>
              <Col md={4}>
                <Form.Check
                  type="switch"
                  id="channel-sms"
                  label="SMS"
                  checked={isOtaTemplate ? false : !!editTemplate.channels?.sms?.enabled}
                  disabled={isOtaTemplate}
                  aria-describedby={isOtaTemplate ? 'ota-channel-note' : undefined}
                  onChange={(e) => setEditTemplate(prev => ({
                    ...prev,
                    channels: {
                      ...(prev.channels || {}),
                      sms: { ...(prev.channels?.sms || {}), enabled: e.target.checked }
                    }
                  }))}
                />
              </Col>
            </Row>

            {isOtaTemplate && (
              <Row className="mb-3">
                <Col md={12}>
                  <Form.Text id="ota-channel-note" className="text-muted">
                    🌐 هذا القالب يُرسل إلى محادثة الضيف على منصة الحجز (Airbnb / Booking.com) عبر أتمتة مخصصة — قناتا واتساب وSMS معطّلتان له.
                  </Form.Text>
                </Col>
              </Row>
            )}

            <Row className="mb-3">
              <Col md={6}>
                <FloatingLabel label="نص الرسالة (عربي)">
                  <Form.Control
                    as="textarea"
                    style={{ height: '140px' }}
                    value={editTemplate.content?.ar?.body || ''}
                    onChange={(e) => setEditTemplate(prev => ({
                      ...prev,
                      content: {
                        ...(prev.content || {}),
                        ar: { ...(prev.content?.ar || {}), body: e.target.value }
                      }
                    }))}
                  />
                </FloatingLabel>
              </Col>
              <Col md={6}>
                <FloatingLabel label="نص الرسالة (إنجليزي)">
                  <Form.Control
                    as="textarea"
                    style={{ height: '140px' }}
                    dir="ltr"
                    value={editTemplate.content?.en?.body || ''}
                    onChange={(e) => setEditTemplate(prev => ({
                      ...prev,
                      content: {
                        ...(prev.content || {}),
                        en: { ...(prev.content?.en || {}), body: e.target.value }
                      }
                    }))}
                  />
                </FloatingLabel>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseEditModal} disabled={savingEdit}>
              إغلاق
            </Button>
            <Button variant="primary" onClick={handleSaveEdit} disabled={savingEdit}>
              {savingEdit ? 'جاري الحفظ...' : 'حفظ التعديلات'}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default TemplatesTab;
